// src/cockpit4/workspaces/components/WeeklyOutlineToolbar.tsx
// ============================================
// 周度内容大纲 · 卡片工具栏（纯展示组件）
//
// 定位：从 MarketingStudioWorkspace 拆分出的展示层——卡片头（周度内容大纲标题 +
// 周次标签 + 提示文案）与输入行（本周重点方向 + 生成本周大纲 + 历史大纲）。
// 不持有任何状态/业务逻辑：全部由父组件经 props + 回调注入。
//
// 视觉：与父文件一致的深色玻璃拟态（purple 主题），字号 8-12px font-mono。
// ============================================

import { CalendarRange, Sparkles, Loader2, FolderArchive } from "lucide-react";

interface WeeklyOutlineToolbarProps {
  /** 周次标签（如 "2026-W42"），为空则不显示 */
  weekLabel?: string;
  /** 本周重点方向输入值 */
  focus: string;
  /** 输入变化回调 */
  onFocusChange: (v: string) => void;
  /** 点击「生成本周大纲」（父组件已包 guard 只读拦截） */
  onGenerate: () => void;
  /** 是否正在生成（按钮显示"生成中..." + Loader2 旋转） */
  generating: boolean;
  /** 老板只读模式：禁用生成按钮并显示只读 title 提示 */
  readonly: boolean;
  /** 点击「历史大纲」 */
  onHistory: () => void;
}

export default function WeeklyOutlineToolbar({
  weekLabel,
  focus,
  onFocusChange,
  onGenerate,
  generating,
  readonly,
  onHistory,
}: WeeklyOutlineToolbarProps) {
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarRange size={12} className="text-purple-400" />
          <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider">周度内容大纲</span>
          {weekLabel && (
            <span className="text-[9px] font-mono text-gray-500">{weekLabel}</span>
          )}
          <span className="text-[8px] font-mono text-gray-600">AI 生成 7 天选题 → 下方直接与 Hermes 讨论 → 确定后各平台生成</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-2">
        <input
          value={focus}
          onChange={(e) => onFocusChange(e.target.value)}
          placeholder="本周重点方向（可留空，默认教程系列+实战经验）"
          className="flex-1 px-2 py-1 text-[10px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600 text-gray-300"
        />
        <button
          onClick={onGenerate}
          disabled={generating || readonly}
          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 rounded transition-colors disabled:opacity-40"
          title={readonly ? "老板只读模式：不可生成内容" : "生成未来 7 天内容大纲（每天 2-3 个主题+平台建议+钩子），写入 Obsidian"}
        >
          {generating ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
          {generating ? "生成中..." : "生成本周大纲"}
        </button>
        {/* v6.2.9 历史大纲：集中查看所有周度大纲（含详细版），可恢复 */}
        <button
          onClick={onHistory}
          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-mono text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded transition-colors"
          title="查看历史周度大纲（每周生成/采纳的都在这里）"
        >
          <FolderArchive size={10} />
          历史大纲
        </button>
      </div>
    </>
  );
}
